/**
 * Mock Scheduled Bookings Data
 * 
 * This simulates bookings created by the Resource Scheduler
 * that will auto-populate into employee timesheets.
 */

import type { ScheduledBooking } from '@/types/scheduledBooking';
import { addDays, startOfWeek, subWeeks } from 'date-fns';

/**
 * Generate scheduled bookings for testing
 * 
 * Creates bookings for various employees across different weeks
 */
export const generateScheduledBookings = (): ScheduledBooking[] => {
  const bookings: ScheduledBooking[] = [];
  const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
  
  // Current week bookings for James Smith (1) - the default logged-in user
  bookings.push(
    {
      id: 1001,
      employeeId: 1,
      employeeName: 'James Smith',
      projectId: 'project-1',
      projectName: 'Cloud Migration',
      projectCode: 'CLOUD-MIG',
      costCentreId: 'cc-pm',
      costCentreName: 'Project Management',
      companyId: '00000000-0000-0000-0000-000000000001',
      weekEnding: addDays(currentWeek, 6),
      scheduledHours: {
        monday: 8,
        tuesday: 8,
        wednesday: 8,
        thursday: 8,
        friday: 8,
        saturday: 0,
        sunday: 0,
      },
      createdBy: '2',
      createdByName: 'Sarah Jones',
      createdAt: subWeeks(new Date(), 1),
      notes: 'Lead project management for Q1 cloud migration delivery',
      status: 'active',
    },
    {
      id: 1002,
      employeeId: 1,
      employeeName: 'James Smith',
      projectId: 'project-5',
      projectName: 'Digital Transformation',
      projectCode: 'DIG-TRANS',
      costCentreId: 'cc-arch',
      costCentreName: 'Architecture',
      companyId: '00000000-0000-0000-0000-000000000001',
      weekEnding: addDays(currentWeek, 6),
      scheduledHours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0,
      },
      createdBy: '2',
      createdByName: 'Sarah Jones',
      createdAt: subWeeks(new Date(), 2),
      notes: 'Architecture oversight - add hours as needed',
      status: 'active',
    }
  );
  
  // Current week bookings for William Johnson (11) - reports to James Smith (1)
  bookings.push(
    {
      id: 1003,
      employeeId: 11,
      employeeName: 'William Johnson',
      projectId: 'project-1',
      projectName: 'Cloud Migration',
      projectCode: 'CLOUD-MIG',
      costCentreId: 'cc-dev',
      costCentreName: 'Development',
      companyId: '00000000-0000-0000-0000-000000000001',
      weekEnding: addDays(currentWeek, 6),
      scheduledHours: {
        monday: 6,
        tuesday: 6,
        wednesday: 6,
        thursday: 6,
        friday: 6,
        saturday: 0,
        sunday: 0,
      },
      createdBy: '1',
      createdByName: 'James Smith',
      createdAt: subWeeks(new Date(), 1),
      notes: 'Sprint 12 delivery work - focus on API migration',
      status: 'active',
    },
    {
      id: 1004,
      employeeId: 11,
      employeeName: 'William Johnson',
      projectId: 'project-5',
      projectName: 'Digital Transformation',
      projectCode: 'DIG-TRANS',
      costCentreId: 'cc-arch',
      costCentreName: 'Architecture',
      companyId: '00000000-0000-0000-0000-000000000001',
      weekEnding: addDays(currentWeek, 6),
      scheduledHours: {
        monday: 2,
        tuesday: 2,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0,
      },
      createdBy: '1',
      createdByName: 'James Smith',
      createdAt: subWeeks(new Date(), 1),
      notes: 'Architecture review sessions - Monday and Tuesday only',
      status: 'active',
    },
    {
      id: 1005,
      employeeId: 11,
      employeeName: 'William Johnson',
      projectId: 'project-12',
      projectName: 'API Integration',
      projectCode: 'API-INT',
      costCentreId: 'cc-dev',
      costCentreName: 'Development',
      companyId: '00000000-0000-0000-0000-000000000001',
      weekEnding: addDays(currentWeek, 6),
      scheduledHours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0,
      },
      createdBy: '1',
      createdByName: 'James Smith',
      createdAt: subWeeks(new Date(), 2),
      notes: 'Placeholder booking - add actual hours worked',
      status: 'active',
    }
  );

  // Current week bookings for Hannah Walker (12) - reports to Sarah Jones (2)
  bookings.push(
    {
      id: 1006,
      employeeId: 12,
      employeeName: 'Hannah Walker',
      projectId: 'project-3',
      projectName: 'System Implementation',
      projectCode: 'SYS-IMPL',
      costCentreId: 'cc-pm',
      costCentreName: 'Project Management',
      companyId: '00000000-0000-0000-0000-000000000001',
      weekEnding: addDays(currentWeek, 6),
      scheduledHours: {
        monday: 8,
        tuesday: 8,
        wednesday: 8,
        thursday: 8,
        friday: 8,
        saturday: 0,
        sunday: 0,
      },
      createdBy: '2',
      createdByName: 'Sarah Jones',
      createdAt: subWeeks(new Date(), 1),
      notes: 'Full-time project management for Q4 delivery',
      status: 'active',
    }
  );

  // Current week bookings for Thomas Wright (13) - reports to Michael Williams (3)
  bookings.push(
    {
      id: 1007,
      employeeId: 13,
      employeeName: 'Thomas Wright',
      projectId: 'project-7',
      projectName: 'Security Assessment',
      projectCode: 'SEC-ASSESS',
      costCentreId: 'cc-dev',
      costCentreName: 'Development',
      companyId: '00000000-0000-0000-0000-000000000001',
      weekEnding: addDays(currentWeek, 6),
      scheduledHours: {
        monday: 4,
        tuesday: 4,
        wednesday: 4,
        thursday: 4,
        friday: 4,
        saturday: 0,
        sunday: 0,
      },
      createdBy: '3',
      createdByName: 'Michael Williams',
      createdAt: subWeeks(new Date(), 1),
      notes: 'Security audit - 4 hours per day',
      status: 'active',
    },
    {
      id: 1008,
      employeeId: 13,
      employeeName: 'Thomas Wright',
      projectId: 'project-15',
      projectName: 'DevOps Transformation',
      projectCode: 'DEVOPS-TRANS',
      costCentreId: 'cc-dev',
      costCentreName: 'Development',
      companyId: '00000000-0000-0000-0000-000000000001',
      weekEnding: addDays(currentWeek, 6),
      scheduledHours: {
        monday: 4,
        tuesday: 4,
        wednesday: 4,
        thursday: 4,
        friday: 4,
        saturday: 0,
        sunday: 0,
      },
      createdBy: '3',
      createdByName: 'Michael Williams',
      createdAt: subWeeks(new Date(), 1),
      notes: 'CI/CD pipeline setup',
      status: 'active',
    }
  );

  // Last week bookings for William Johnson (for testing copy functionality)
  const lastWeek = subWeeks(currentWeek, 1);
  bookings.push(
    {
      id: 1009,
      employeeId: 11,
      employeeName: 'William Johnson',
      projectId: 'project-1',
      projectName: 'Cloud Migration',
      projectCode: 'CLOUD-MIG',
      costCentreId: 'cc-dev',
      costCentreName: 'Development',
      companyId: '00000000-0000-0000-0000-000000000001',
      weekEnding: addDays(lastWeek, 6),
      scheduledHours: {
        monday: 6,
        tuesday: 6,
        wednesday: 6,
        thursday: 6,
        friday: 6,
        saturday: 0,
        sunday: 0,
      },
      createdBy: '1',
      createdByName: 'James Smith',
      createdAt: subWeeks(new Date(), 2),
      notes: 'Sprint 11 delivery work',
      status: 'completed',
    }
  );

  return bookings;
};

/**
 * Get scheduled bookings for a specific employee and week
 */
export const getScheduledBookingsForWeek = (
  employeeId: number,
  weekEnding: Date,
  allBookings: ScheduledBooking[]
): ScheduledBooking[] => {
  return allBookings.filter(
    booking =>
      booking.employeeId === employeeId &&
      booking.weekEnding.getTime() === weekEnding.getTime() &&
      booking.status === 'active'
  );
};

/**
 * Mock scheduled bookings data
 */
export const mockScheduledBookings = generateScheduledBookings();
